import {
    ATTRIBUTION_VALUE_PATTERN,
    LANDING_PATH_PATTERN,
    SIGNUP_ATTRIBUTION_COOKIE,
    SIGNUP_ATTRIBUTION_MAX_AGE_SECONDS,
} from "@/constants/signupAttribution.constants"
import type { SignupAttribution } from "@/schemas/signupAttribution.schema"

const UTM_PARAMS = {
    source: "utm_source",
    medium: "utm_medium",
    campaign: "utm_campaign",
    content: "utm_content",
    term: "utm_term",
} as const

export const normalizedAttributionValue = (
    raw: string | null | undefined
): string | undefined => {
    const value = raw?.trim().toLowerCase()
    return value && ATTRIBUTION_VALUE_PATTERN.test(value) ? value : undefined
}

export const signupAttributionFromUrl = ({
    url,
    now,
}: {
    url: URL
    now: Date
}): SignupAttribution | null => {
    const valueOf = (param: string) =>
        normalizedAttributionValue(url.searchParams.get(param))
    const source = valueOf(UTM_PARAMS.source)
    if (!source) return null
    const optional = {
        medium: valueOf(UTM_PARAMS.medium),
        campaign: valueOf(UTM_PARAMS.campaign),
        content: valueOf(UTM_PARAMS.content),
        term: valueOf(UTM_PARAMS.term),
    }
    return {
        source,
        ...Object.fromEntries(
            Object.entries(optional).filter(([, value]) => value !== undefined)
        ),
        landingPath: LANDING_PATH_PATTERN.test(url.pathname)
            ? url.pathname
            : "/",
        firstSeenAt: now.toISOString(),
    }
}

const storedCookieValue = (cookieHeader: string): string | undefined => {
    const prefix = `${SIGNUP_ATTRIBUTION_COOKIE}=`
    const entry = cookieHeader
        .split(";")
        .map(part => part.trim())
        .find(part => part.startsWith(prefix))
    if (entry === undefined) return undefined
    try {
        return decodeURIComponent(entry.slice(prefix.length))
    } catch {
        // A malformed escape is a value this app never wrote, read as absent
        return undefined
    }
}

const isValidAttributionValue = (value: unknown): boolean =>
    typeof value === "string" && normalizedAttributionValue(value) !== undefined

const isOptionalAttributionValue = (value: unknown): boolean =>
    value === undefined || isValidAttributionValue(value)

const isIsoTimestamp = (value: unknown): boolean => {
    if (typeof value !== "string") return false
    const parsed = new Date(value)
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value
}

const hasStoredFirstTouchIn = (raw: string | undefined): boolean => {
    if (!raw) return false
    let stored: unknown
    try {
        stored = JSON.parse(raw)
    } catch {
        // A cookie that is not JSON holds no first touch, so a new link may replace it
        return false
    }
    if (typeof stored !== "object" || stored === null) return false
    const fields = stored as Record<string, unknown>
    return (
        isValidAttributionValue(fields.source) &&
        isOptionalAttributionValue(fields.medium) &&
        isOptionalAttributionValue(fields.campaign) &&
        isOptionalAttributionValue(fields.content) &&
        isOptionalAttributionValue(fields.term) &&
        typeof fields.landingPath === "string" &&
        LANDING_PATH_PATTERN.test(fields.landingPath) &&
        isIsoTimestamp(fields.firstSeenAt)
    )
}

export const firstTouchSignupAttributionCookie = ({
    url,
    cookieHeader,
    now,
}: {
    url: URL
    cookieHeader: string
    now: Date
}): string | null => {
    if (hasStoredFirstTouchIn(storedCookieValue(cookieHeader))) return null
    const attribution = signupAttributionFromUrl({ url, now })
    if (!attribution) return null
    const secure = url.protocol === "https:" ? "; secure" : ""
    return `${SIGNUP_ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(attribution))}; path=/; max-age=${SIGNUP_ATTRIBUTION_MAX_AGE_SECONDS}; samesite=lax${secure}`
}

export const signupAttributionAnalyticsProperties = (
    attribution: SignupAttribution | null | undefined
): Record<string, string> => {
    if (!attribution) return {}
    const properties: Record<string, string | undefined> = {
        signupSource: attribution.source,
        signupMedium: attribution.medium,
        signupCampaign: attribution.campaign,
        signupContent: attribution.content,
        signupTerm: attribution.term,
        signupLandingPath: attribution.landingPath,
    }
    return Object.fromEntries(
        Object.entries(properties).filter(
            (entry): entry is [string, string] => entry[1] !== undefined
        )
    )
}
